import { invariantResponse } from '@epic-web/invariant'
import { ArrowLeftIcon } from '@radix-ui/react-icons'
import ReactMarkdown, { type Components } from 'react-markdown'
import {
	data,
	type LinksFunction,
	type LoaderFunctionArgs,
	type MetaFunction,
	Link,
	useLoaderData,
} from 'react-router'
import { ErrorMessage, GeneralErrorBoundary } from '#app/components/templates'
import { getLatestContentRevision } from '#app/utils/content-revision.server.ts'
import { prisma } from '#app/utils/db.server.js'
import { requireFestivalVisible } from '#app/utils/maintenance.server.ts'
import { canUserEditPerformer } from '#app/domains/performer'
import {
	festivalParts,
	formatClock,
	formatDate,
	formatDayMonth,
	formatShortWeekday,
	formatWeekday,
	isEventPassed,
	parseSafeZone,
	performerShareCardPath,
	SHARE_CARD_SIZE,
	safeZoneImageStyle,
} from '#app/domains/program/pure'
import performersStyleUrl from '../../styles/performers.css?url'
import { pageMeta } from '#app/utils/page-meta.ts'

export const links: LinksFunction = () => [
	{ rel: 'stylesheet', href: performersStyleUrl },
]

const markdownComponents: Components = {
	a: ({ href, children }) => (
		<a href={href} target="_blank" rel="noopener noreferrer">
			{children}
		</a>
	),
	img: ({ src, alt }) => (
		<img src={src} alt={alt} className="pf-md-img" loading="lazy" />
	),
}

export async function loader({ request, params }: LoaderFunctionArgs) {
	await requireFestivalVisible(request)
	const performer = await prisma.performer.findUnique({
		where: { slug: params.slug },
		include: {
			events: { orderBy: { date: 'asc' }, include: { seats: true } },
		},
	})
	invariantResponse(performer, 'Fann ikkje artisten', { status: 404 })
	const [canEdit, lastRevision] = await Promise.all([
		canUserEditPerformer(request, performer.id),
		getLatestContentRevision(performer.id),
	])
	return data({
		performer,
		canEdit,
		lastRevision,
		shareImage: performerShareCardPath(performer),
	})
}

export const meta: MetaFunction<typeof loader> = (args) => {
	if (!args.loaderData?.performer) return pageMeta(args, { title: 'Artist' })
	const { performer, shareImage } = args.loaderData
	return pageMeta(args, {
		title: performer.name,
		description:
			performer.shortBio ?? `Møt ${performer.name} på Huskonsertfestivalen.`,
		image: {
			url: shareImage,
			...SHARE_CARD_SIZE,
			alt: `${performer.name} — artist på Huskonsertfestivalen`,
			type: 'image/jpeg',
		},
	})
}

export default function PerformerPage() {
	const { performer, canEdit, lastRevision } = useLoaderData<typeof loader>()
	const upcoming = performer.events.filter(
		(event) => !isEventPassed(event.date),
	)
	const past = performer.events.filter((event) => isEventPassed(event.date))

	return (
		<main className="pf pf-page">
			<header className="pf-hero">
				<Link to="/performers" viewTransition className="pf-back">
					<ArrowLeftIcon aria-hidden="true" /> Artistane
				</Link>
				<div className="pf-hero-row">
					<span className="pf-hero-portrait">
						{performer.imageUrl ? (
							<img
								src={performer.imageUrl}
								alt={performer.name}
								style={{
									viewTransitionName: `performer-photo-${performer.slug}`,
									...safeZoneImageStyle(
										parseSafeZone(performer.imageSafeZone),
										1,
									),
								}}
							/>
						) : (
							<span className="pf-monogram" aria-hidden="true">
								{performer.name
									.trim()
									.split(/\s+/)
									.slice(0, 2)
									.map((part) => part.charAt(0).toUpperCase())
									.join('')}
							</span>
						)}
					</span>
					<div className="pf-hero-text">
						<h1
							className="pf-hero-name"
							style={{ viewTransitionName: `performer-${performer.slug}` }}
						>
							{performer.name}
						</h1>
						{performer.shortBio ? (
							<p className="pf-hero-bio">{performer.shortBio}</p>
						) : null}
						{canEdit || lastRevision ? (
							<p className="pf-tools">
								{canEdit ? (
									<Link to={`/performers/${performer.slug}/edit`}>Rediger</Link>
								) : null}
								{lastRevision ? (
									<Link to={`/performers/${performer.slug}/history`}>
										Historikk
									</Link>
								) : null}
							</p>
						) : null}
					</div>
				</div>
			</header>

			{performer.bio ? (
				<div className="pf-md">
					<ReactMarkdown components={markdownComponents}>
						{performer.bio}
					</ReactMarkdown>
				</div>
			) : null}

			{upcoming.length > 0 ? (
				<section className="pf-section" aria-labelledby="pf-upcoming">
					<h2 id="pf-upcoming" className="pf-h2">
						Konsertar i år
					</h2>
					<EventList events={upcoming} />
				</section>
			) : null}
			{past.length > 0 ? (
				<section className="pf-section pf-past" aria-labelledby="pf-past">
					<h2 id="pf-past" className="pf-h2">
						Tidlegare
					</h2>
					<EventList events={past} />
				</section>
			) : null}

			{lastRevision ? (
				<p className="pf-fine">
					Sist endra av{' '}
					<Link to={`/performers/${performer.slug}/history`}>
						{lastRevision.editedByName ?? 'ukjend'}
					</Link>{' '}
					{formatDate(lastRevision.createdAt)}
				</p>
			) : null}
		</main>
	)
}

function EventList({ events }: { events: LoaderDataEvent[] }) {
	return (
		<ul className="pf-gig-list">
			{events.map((event) => (
				<li key={event.id}>
					<Link to={`/events/${event.id}`} viewTransition className="pf-gig">
						<span className="pf-gig-stub" aria-hidden="true">
							<span>{formatShortWeekday(event.date).replace('.', '')}</span>
							<strong>{festivalParts(event.date).day}</strong>
						</span>
						<span className="pf-gig-text">
							<strong style={{ viewTransitionName: `event-title-${event.id}` }}>
								{event.title}
							</strong>
							<small>
								{formatWeekday(event.date)} {formatDayMonth(event.date)}{' '}
								{festivalParts(event.date).year} · kl. {formatClock(event.date)}
							</small>
						</span>
					</Link>
				</li>
			))}
		</ul>
	)
}

interface LoaderDataEvent {
	id: string
	title: string
	date: Date
}

export function ErrorBoundary() {
	return (
		<GeneralErrorBoundary
			statusHandlers={{
				404: ({ params }) => (
					<ErrorMessage
						code={404}
						title="Fann ikkje artisten"
						text={`Ingen artist heiter «${params.slug}». Sjå alle artistane i staden.`}
					/>
				),
			}}
		/>
	)
}
